import { computed, h } from 'vue';
import { useI18n } from 'vue-i18n';

/**
 * @typedef {Object} FilterOperations
 * @property {string} EQUAL_TO - Equals comparison
 * @property {string} NOT_EQUAL_TO - Not equals comparison
 * @property {string} IS_PRESENT - Present check
 * @property {string} IS_NOT_PRESENT - Not present check
 * @property {string} CONTAINS - Contains check
 * @property {string} DOES_NOT_CONTAIN - Does not contain check
 * @property {string} IS_GREATER_THAN - Greater than comparison
 * @property {string} IS_LESS_THAN - Less than comparison
 * @property {string} DAYS_BEFORE - Days before check
 * @property {string} STARTS_WITH - Starts with check
 */

/**
 * @typedef {Object} Operator
 * @property {string} value - Operator value from FILTER_OPS
 * @property {string} label - Translated display label
 * @property {import('vue').VNode} icon - Vue icon component instance
 * @property {string|null} inputOverride - Input field type override
 * @property {boolean} hasInput - Whether operator requires an input value
 */

const FILTER_OPS = {
  EQUAL_TO: 'equal_to',
  NOT_EQUAL_TO: 'not_equal_to',
  IS_PRESENT: 'is_present',
  IS_NOT_PRESENT: 'is_not_present',
  CONTAINS: 'contains',
  DOES_NOT_CONTAIN: 'does_not_contain',
  IS_GREATER_THAN: 'is_greater_than',
  IS_GREATER_THAN_OR_EQUAL: 'is_greater_than_or_equal',
  IS_LESS_THAN: 'is_less_than',
  IS_LESS_THAN_OR_EQUAL: 'is_less_than_or_equal',
  DAYS_BEFORE: 'days_before',
  STARTS_WITH: 'starts_with',

  // Novos operadores personalizados
  IS_TODAY: 'is_today',
  DUE_IN_DAYS: 'due_in_days',
  DUE_X_DAYS_AGO: 'due_x_days_ago',
  DUE_IN_RANGE: 'due_in_range',
  OVERDUE_RANGE: 'overdue_range',
};

const NO_INPUT_OPTS = [FILTER_OPS.IS_PRESENT, FILTER_OPS.IS_NOT_PRESENT];

const OPS_INPUT_OVERRIDE = {
  [FILTER_OPS.DAYS_BEFORE]: 'plainText',
  [FILTER_OPS.DUE_IN_DAYS]: 'number',
  [FILTER_OPS.DUE_X_DAYS_AGO]: 'number',
};

/**
 * Ícone por operador. Exportado porque o `FilterSelect` também precisa
 * resolver ícone a partir do `option.value` — ele mantinha uma cópia própria
 * deste mapa e operador novo caía no funil genérico até alguém lembrar de
 * atualizar os dois lados. Fonte única.
 * @type {Record<string, string>}
 */
export const filterOperatorIcon = {
  [FILTER_OPS.EQUAL_TO]: 'i-ph-equals-bold',
  [FILTER_OPS.NOT_EQUAL_TO]: 'i-ph-not-equals-bold',
  [FILTER_OPS.IS_PRESENT]: 'i-ph-member-of-bold',
  [FILTER_OPS.IS_NOT_PRESENT]: 'i-ph-not-member-of-bold',
  [FILTER_OPS.CONTAINS]: 'i-ph-superset-of-bold',
  [FILTER_OPS.DOES_NOT_CONTAIN]: 'i-ph-not-superset-of-bold',
  [FILTER_OPS.IS_GREATER_THAN]: 'i-ph-greater-than-bold',
  [FILTER_OPS.IS_GREATER_THAN_OR_EQUAL]: 'i-ph-greater-than-or-equal-bold',
  [FILTER_OPS.IS_LESS_THAN]: 'i-ph-less-than-bold',
  [FILTER_OPS.IS_LESS_THAN_OR_EQUAL]: 'i-ph-less-than-or-equal-bold',
  [FILTER_OPS.DAYS_BEFORE]: 'i-ph-calendar-minus-bold',
  [FILTER_OPS.STARTS_WITH]: 'i-ph-caret-line-right-bold',
  [FILTER_OPS.DUE_IN_DAYS]: 'i-ph-calendar-plus-bold',
  [FILTER_OPS.DUE_X_DAYS_AGO]: 'i-ph-calendar-minus-bold',
  [FILTER_OPS.IS_TODAY]: 'i-ph-calendar-check-bold',
  [FILTER_OPS.DUE_IN_RANGE]: 'i-ph-calendar-dots-bold',
  [FILTER_OPS.OVERDUE_RANGE]: 'i-ph-calendar-x-bold',
};

/**
 * Vue composable providing access to filter operators and related functionality
 * @returns {Object} Collection of operators and utility functions
 * @property {import('vue').ComputedRef<Record<string, Operator>>} operators - All available operators
 * @property {import('vue').ComputedRef<Operator[]>} equalityOperators - Equality comparison operators
 * @property {import('vue').ComputedRef<Operator[]>} presenceOperators - Presence check operators
 * @property {import('vue').ComputedRef<Operator[]>} containmentOperators - Containment check operators
 * @property {import('vue').ComputedRef<Operator[]>} comparisonOperators - Numeric comparison operators
 * @property {import('vue').ComputedRef<Operator[]>} numericOperators - Ordering operators (>, >=, <, <=) for numeric columns
 * @property {import('vue').ComputedRef<Operator[]>} dateOperators - Date-specific operators
 * @property {(key: 'list'|'text'|'number'|'dbNumericColumn'|'dbDateColumn'|'link'|'date'|'checkbox') => Operator[]} getOperatorTypes - Get operators for a field type
 */
export function useOperators() {
  const { t } = useI18n();

  /** @type {import('vue').ComputedRef<Record<string, Operator>>} */
  const operators = computed(() => {
    return Object.values(FILTER_OPS).reduce((acc, value) => {
      acc[value] = {
        value,
        label: t(`FILTER.OPERATOR_LABELS.${value}`),
        hasInput: !NO_INPUT_OPTS.includes(value),
        inputOverride: OPS_INPUT_OVERRIDE[value] || null,
        icon: h('span', {
          class: `${filterOperatorIcon[value]} !text-n-blue-text`,
        }),
      };
      return acc;
    }, {});
  });

  /** @type {import('vue').ComputedRef<Array<Operator>>} */
  const equalityOperators = computed(() => [
    operators.value[FILTER_OPS.EQUAL_TO],
    operators.value[FILTER_OPS.NOT_EQUAL_TO],
  ]);

  /** @type {import('vue').ComputedRef<Array<Operator>>} */
  const presenceOperators = computed(() => [
    operators.value[FILTER_OPS.EQUAL_TO],
    operators.value[FILTER_OPS.NOT_EQUAL_TO],
    operators.value[FILTER_OPS.IS_PRESENT],
    operators.value[FILTER_OPS.IS_NOT_PRESENT],
  ]);

  /** @type {import('vue').ComputedRef<Array<Operator>>} */
  const containmentOperators = computed(() => [
    operators.value[FILTER_OPS.EQUAL_TO],
    operators.value[FILTER_OPS.NOT_EQUAL_TO],
    operators.value[FILTER_OPS.CONTAINS],
    operators.value[FILTER_OPS.DOES_NOT_CONTAIN],
  ]);

  /** @type {import('vue').ComputedRef<Array<Operator>>} */
  const comparisonOperators = computed(() => [
    operators.value[FILTER_OPS.EQUAL_TO],
    operators.value[FILTER_OPS.NOT_EQUAL_TO],
    operators.value[FILTER_OPS.IS_PRESENT],
    operators.value[FILTER_OPS.IS_NOT_PRESENT],
    operators.value[FILTER_OPS.IS_GREATER_THAN],
    operators.value[FILTER_OPS.IS_LESS_THAN],
  ]);

  /**
   * Coluna numérica (integer/numeric/float): além da igualdade, as quatro
   * comparações de ordem. Os operadores existem no
   * `config/filter_operators.yml` dos dois adapters — mudar esta lista sem
   * mexer no yml gera filtro que o backend rejeita.
   * @type {import('vue').ComputedRef<Array<Operator>>}
   */
  const numericOperators = computed(() => [
    operators.value[FILTER_OPS.EQUAL_TO],
    operators.value[FILTER_OPS.NOT_EQUAL_TO],
    operators.value[FILTER_OPS.IS_GREATER_THAN],
    operators.value[FILTER_OPS.IS_GREATER_THAN_OR_EQUAL],
    operators.value[FILTER_OPS.IS_LESS_THAN],
    operators.value[FILTER_OPS.IS_LESS_THAN_OR_EQUAL],
    operators.value[FILTER_OPS.IS_PRESENT],
    operators.value[FILTER_OPS.IS_NOT_PRESENT],
  ]);

  /** @type {import('vue').ComputedRef<Array<Operator>>} */
  const dateOperators = computed(() => [
    operators.value[FILTER_OPS.IS_GREATER_THAN],
    operators.value[FILTER_OPS.IS_LESS_THAN],
    operators.value[FILTER_OPS.EQUAL_TO],
    operators.value[FILTER_OPS.DUE_IN_DAYS],
    operators.value[FILTER_OPS.DUE_X_DAYS_AGO],
  ]);

  /**
   * Coluna DATE/TIMESTAMP de banco de terceiro (wizard/builder de campanha):
   * a lista de data do core MAIS >= e <= — o DatabaseFilterService valida
   * pelo config/filter_operators.yml, que tem os dois nos dois adapters. A
   * `dateOperators` do core fica intocada: atributo de data de conversa/
   * contato passa pelo FilterService, que não cobre >=/<= (mesma separação
   * do par `number` × `dbNumericColumn` documentada no getOperatorTypes).
   * @type {import('vue').ComputedRef<Array<Operator>>}
   */
  const dbDateOperators = computed(() => [
    operators.value[FILTER_OPS.EQUAL_TO],
    operators.value[FILTER_OPS.IS_GREATER_THAN],
    operators.value[FILTER_OPS.IS_GREATER_THAN_OR_EQUAL],
    operators.value[FILTER_OPS.IS_LESS_THAN],
    operators.value[FILTER_OPS.IS_LESS_THAN_OR_EQUAL],
    operators.value[FILTER_OPS.DUE_IN_DAYS],
    operators.value[FILTER_OPS.DUE_X_DAYS_AGO],
  ]);

  /**
   * Get operator types based on key
   * @param {string} key - Type of operator to get
   * @returns {Array<Operator>}
   */
  const getOperatorTypes = key => {
    switch (key) {
      case 'list':
        return equalityOperators.value;
      case 'text':
        return containmentOperators.value;
      // `number` é o custom attribute do core (contatos/conversas), filtrado
      // pelo FilterService do Rails — que não aceita ordenação nesse campo, daí
      // só igualdade. `dbNumericColumn` é coluna de banco de terceiro no wizard
      // de campanha, que passa pelo DatabaseFilterService e tem os operadores de
      // ordem no config/filter_operators.yml. Backends distintos, listas
      // distintas: os nomes têm que dizer isso, senão o par vira `number` ×
      // `numeric` e quem chega depois escolhe no chute.
      case 'number':
        return equalityOperators.value;
      case 'dbNumericColumn':
        return numericOperators.value;
      case 'dbDateColumn':
        return dbDateOperators.value;
      case 'link':
        return equalityOperators.value;
      case 'date':
        return comparisonOperators.value;
      case 'checkbox':
        return equalityOperators.value;
      default:
        return equalityOperators.value;
    }
  };

  return {
    operators,
    equalityOperators,
    presenceOperators,
    containmentOperators,
    comparisonOperators,
    numericOperators,
    dateOperators,
    dbDateOperators,
    getOperatorTypes,
  };
}
